// Phase 15 Plan 05 Task 2 — $lobbyData store + visibility-aware polling.
//
// Single source for the lobby/home batched payload (HomeResponse). Read by:
//   - LobbyScreen (Plan 06)
//   - $activeMatchCount + $activeMatchCap + $isAtCap (DEC-009 selectors)
//   - $tournamentList helper (reads `active_tournaments`)
//
// Polling cadence: 30s while the tab is foregrounded. Pauses when hidden
// (Pitfall 6 in 15-RESEARCH.md — avoid DO write traffic from background
// tabs). Resumes + triggers an immediate refresh on visible.
//
// EPHEMERAL — `atom()` (not `persistentAtom`); a page reload re-fetches
// via the auth gate. Single in-memory cache, no localStorage.

import { atom } from 'nanostores'
import { fetchHome, type HomeResponse } from '../net/home'

export const $lobbyData = atom<HomeResponse | null>(null)

/** Default lobby refresh cadence (ms). */
export const LOBBY_POLL_MS = 30_000

let pollTimer: ReturnType<typeof setInterval> | null = null
// WR-07: refcount-based init/teardown so multiple concurrent
// `initLobbyPolling` callers (e.g. simultaneously-mounted screens) do not
// stomp on shared state. The interval + visibilitychange listener are
// installed only on the FIRST init and torn down only when the LAST
// teardown fires. Each call returns a single-shot teardown that decrements
// once.
let pollRefcount = 0
let visibilityListener: (() => void) | null = null

export function setLobbyData(data: HomeResponse | null): void {
  $lobbyData.set(data)
}

/**
 * Fetch the current /api/home payload + write it into $lobbyData. Errors
 * are swallowed (the prior value stays put); apiFetch already retries 5xx
 * once and clears auth on 401.
 */
export async function refreshLobby(): Promise<void> {
  try {
    const data = await fetchHome()
    $lobbyData.set(data)
  } catch {
    // apiFetch already retried 5xx + cleared auth on 401. We do NOT blank
    // the screen — keep prior value so the UI doesn't flicker.
  }
}

function isVisible(): boolean {
  return (
    typeof document === 'undefined' || document.visibilityState === 'visible'
  )
}

function startPolling(): void {
  if (pollTimer !== null) return
  pollTimer = setInterval(() => {
    void refreshLobby()
  }, LOBBY_POLL_MS)
}

function stopPolling(): void {
  if (pollTimer === null) return
  clearInterval(pollTimer)
  pollTimer = null
}

/**
 * Start visibility-aware lobby polling — refcounted (WR-07).
 *
 * Behavior:
 *   - Immediately fires a refresh + starts the 30s interval if visible
 *     (only when refcount goes from 0 → 1).
 *   - Pauses (clears the interval) when `document.visibilityState ===
 *     'hidden'`. Resumes + triggers an immediate refresh on visible.
 *   - Returns a teardown that decrements the refcount. When the refcount
 *     reaches 0 the interval is cleared and the listener removed. The
 *     teardown is single-shot (calling it twice decrements only once;
 *     guard via closure flag).
 *
 * Multiple concurrent callers — e.g. simultaneously-mounted lobby screens
 * during a route transition — share the same interval and one
 * visibilitychange listener. The previous module-level shared state
 * leaked the interval when a later teardown ran AFTER an earlier init
 * had already shut things down.
 */
export function initLobbyPolling(): () => void {
  pollRefcount += 1

  if (pollRefcount === 1) {
    // First init — install listener + start the timer if visible.
    if (isVisible()) {
      void refreshLobby()
      startPolling()
    }

    const onVisibilityChange = (): void => {
      if (isVisible()) {
        void refreshLobby()
        startPolling()
      } else {
        stopPolling()
      }
    }
    visibilityListener = onVisibilityChange

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange)
    }
  }

  let tornDown = false
  return (): void => {
    if (tornDown) return
    tornDown = true
    pollRefcount = Math.max(0, pollRefcount - 1)

    if (pollRefcount === 0) {
      // Last teardown — uninstall listener + clear the timer.
      if (typeof document !== 'undefined' && visibilityListener !== null) {
        document.removeEventListener('visibilitychange', visibilityListener)
      }
      visibilityListener = null
      stopPolling()
    }
  }
}
